import { boolToRadio, radioToNullableBool } from "@/lib/helpers/radio-adapters";
import dayjs from "@/lib/helpers/dayjs";

import {
  ACAA_SCREENING_FIELDS,
  ACP_BOOLEAN_RADIO_FIELDS,
  ADVANCED_CARE_ACTIVITY_INITIAL_VALUES,
} from "../constants";
import type { NoteContent, NoteWithContent } from "../types";

// One-level-deep backfill: starts from `source` (preserving any of its own
// keys, including ones `defaults` doesn't know about — e.g. note metadata),
// then fills in `defaults[key]` only where `source` is missing that key
// entirely OR has an explicit null/undefined there. A plain
// `{...defaults, ...source}` spread would instead let an explicit `null`
// (e.g. an unsigned draft's `signature: null`) clobber the default — this
// avoids that while still guaranteeing every default key ends up present.
const withDefaults = <T extends Record<string, unknown>>(
  defaults: T,
  source: Record<string, unknown> | undefined,
): T & Record<string, unknown> => {
  const result: Record<string, unknown> = { ...source };

  Object.keys(defaults).forEach((key) => {
    if (result[key] === null || result[key] === undefined) {
      result[key] = defaults[key];
    }
  });

  return result as T & Record<string, unknown>;
};

export const adaptAcaaData = (note: Nullable<NoteWithContent>): Nullable<NoteWithContent> => {
  if (!note) return note; // defensive: note may be null upstream

  // Backfill any field missing from `note` with its default (two-level
  // merge mirrors the shape: top-level fields, plus one nested `content`
  // object). A draft created via the video-call flow's `content: {}` POST
  // (see ProviderVideoWorkflowStep.js) can persist with fields like
  // session_from/session_to entirely absent — not "", not null, just
  // missing. Formik's submit-time touch pass (setNestedObjectValues) only
  // visits keys that exist on `values`, so a missing-but-required field
  // never gets touched, never renders its error, and silently blocks
  // Sign & Submit with no visible feedback. Defaulting every key here
  // (rather than just `signature`, as before) closes that gap for all
  // fields, not just the one we happened to notice.
  const copyOfNote: NoteWithContent & { content: NoteContent } = {
    ...withDefaults(ADVANCED_CARE_ACTIVITY_INITIAL_VALUES, note),
    content: withDefaults(ADVANCED_CARE_ACTIVITY_INITIAL_VALUES.content, note.content),
  };

  // Backend stores ACAA yes/no fields as booleans; form radios need
  // "Yes"/"No" strings. Maps boolean -> string for read-side use
  ACP_BOOLEAN_RADIO_FIELDS.forEach((item: string) => {
    copyOfNote.content[item] = boolToRadio(copyOfNote.content[item]);
  });

  // session_date: backend uses "MM/DD/YYYY", form field expects
  // "MMDDYYYY" digits-only. Read-side half of that round-trip.
  const sessionDate = copyOfNote.session_date;

  if (typeof sessionDate === "string" && sessionDate.includes("/")) {
    // strict mode rejects invalid calendar dates instead of rolling them over
    const parsed = dayjs(sessionDate, "MM/DD/YYYY", true);

    if (parsed.isValid()) {
      copyOfNote.session_date = parsed.format("MMDDYYYY");
    }
  }

  return copyOfNote;
};

// casts "Yes"/"No" radio strings back to booleans before submission.
// A cleared/unanswered radio ("" sentinel) drops the key entirely for most
// fields — this only fires on an unanswered draft save (Save Draft skips yup
// validation) since every other ACP_BOOLEAN_RADIO_FIELDS entry is yup-required
// on finalize. ACAA_SCREENING_FIELDS is the exception: those 4 fields must
// submit as explicit null instead of being omitted, since the backend schema
// only widens their type to nullable-boolean (vs. plain boolean) — it never
// drops the required check. That's true on every draft save, and on finalize
// specifically when patient_participant_attest is "No" (no-show).
export const adaptRadioFields = (content: NoteContent): NoteContent => {
  const copyOfContent = { ...content };

  ACP_BOOLEAN_RADIO_FIELDS.forEach((field: string) => {
    const value = radioToNullableBool(copyOfContent[field]);

    if (value === null && !ACAA_SCREENING_FIELDS.includes(field)) {
      delete copyOfContent[field];
    } else {
      copyOfContent[field] = value;
    }
  });

  return copyOfContent;
};
